import { ComponentPropsWithoutRef, forwardRef } from "react";
import cx from "classnames";

import styles from "./Skeleton.module.scss";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../internal/hooks";

/**
 * Props for the SkeletonCircle component
 * @extends Omit<ComponentPropsWithoutRef<"div">, "children">
 * @extends Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink">
 */
export type SkeletonCircleProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "children"
> &
  Omit<LayoutUtilProps, "flex" | "flexBasis" | "flexGrow" | "flexShrink"> & {
    /**
     * The diameter of the circular skeleton in CSS units.
     * @default 32px
     */
    diameter?: string;
    /**
     * Enables the AI visual style with blue gradient colors.
     */
    ai?: boolean;
  };

/**
 * SkeletonCircle component for displaying circular loading placeholders.
 *
 * Features:
 * - Circular skeleton placeholder with customizable diameter
 * - Animated pulsing effect for loading indication
 * - Accessible with proper ARIA attributes
 * - Layout utility props for positioning and spacing
 * - Customizable styling through className and style props
 * - Progress bar role for screen readers
 *
 * @example
 * <Skeleton.Circle diameter="40px" />
 *
 * <Skeleton.Circle
 *   diameter="60px"
 *   className="custom-skeleton"
 *   aria-label="Loading avatar"
 * />
 */
export const SkeletonCircle = forwardRef<HTMLDivElement, SkeletonCircleProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      className,
      diameter,
      ai,
      "aria-label": ariaLabel = "Loading content",
      style,
      ...rest
    } = componentProps;

    const SkeletonClassNames = cx(
      [styles["skeleton"]],
      [styles["circle"]],
      styles["shade"],
      ai && styles["ai"],
      className,
    );

    const styleCombined = {
      ...style,
      ...layoutStyles,
      "--diameter": diameter,
    };

    return (
      <div
        {...rest}
        data-anv="skeleton-circle"
        role="progressbar"
        aria-busy="true"
        className={SkeletonClassNames}
        style={styleCombined}
        aria-label={ariaLabel}
        ref={ref}
      />
    );
  },
);

SkeletonCircle.displayName = "SkeletonCircle";
